import { convertUserToDraft } from "@/modules/users/entities/usersEntity"
import { useRegisterUser } from "@/modules/users/hooks/useRegisterUser"
import { UserErrorCodes } from "@modules/auth/authErrorTypes"
import { AuthContent, AuthForm, AuthHeader, AuthPage } from "@modules/auth/components/authComponents"
import { RegisterFormFourPage } from "@modules/auth/page/registerFormFourPage"
import { RegisterFormOnePage } from "@modules/auth/page/registerFormOnePage"
import { RegisterFormThreePage } from "@modules/auth/page/registerFormThreePage"
import { RegisterFormTwoPage } from "@modules/auth/page/registerFormTwoPage"
import {
  type ProfileRegisterFormikType,
  type ProfileRegisterInputKey,
  convertProfileFormFormik,
  convertProfileToFormik,
  getValidationSchema,
} from "@modules/auth/profileRegisterFormikTypes"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { Path } from "@modules/navigation/routes"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import { AxiosError } from "axios"
import { useFormik } from "formik"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"

export function RegisterPage() {
  const [error, setError] = useState("")
  const [searchParams, setSearchParams] = useSearchParams()
  const popup = useConfirmPopup()
  const navigate = useNavigate()
  const { mutateAsync: registerUserMutation } = useRegisterUser()

  const [pageNumber, setPageNumber] = useState(Number(searchParams.get("page") ?? 1))

  useEffect(() => {
    if (searchParams.get("page")) {
      setPageNumber(Number(searchParams.get("page")))
    } else {
      setSearchParams({ page: pageNumber.toString() })
    }
  }, [searchParams])

  useEffect(() => {
    setSearchParams({ page: pageNumber.toString() })
  }, [pageNumber])

  const navigateForm = (onNext: boolean) => {
    setPageNumber((prev) => {
      const newPage = onNext ? prev + 1 : prev - 1
      return newPage
    })
  }
  const formik = useFormik<ProfileRegisterFormikType>({
    initialValues: convertProfileToFormik(convertUserToDraft(null)),
    validationSchema: getValidationSchema(pageNumber),
    onSubmit: async (values) => {
      try {
        await registerUserMutation(convertProfileFormFormik(values))
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          if (error.code === UserErrorCodes.UsernameAlreadyExists) {
            setError(word("auth.error.username_exists"))
          } else {
            setError(word("global.error.default"))
          }
        } else {
          console.error(LoggerType.SignUp + "register", String(error))
        }
      }
    },
  })

  const handleClose = () =>
    popup.show({
      title: word("auth.signup.form.leaveSignUp.title"),
      message: word("auth.signup.form.leaveSignUp.description"),
      accept: () => {
        void navigate(Path.Login)
      },
    })

  const FormPage = () => {
    const props = {
      handleSubmit: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e?.preventDefault()
        e?.stopPropagation()

        if (pageNumber < 4) {
          navigateForm(true)
          return
        } else formik.handleSubmit()
      },
      onPrevious: pageNumber === 1 ? handleClose : () => navigateForm(false),
      isFormFieldInValid: (input: ProfileRegisterInputKey) => isFormFieldInValid(input, formik),
      getFormErrorMessage: (input: ProfileRegisterInputKey) => getFormErrorMessage(input, formik),
      disabled: !getValidationSchema(pageNumber).isValidSync(formik.values),
      formik: formik,
      isSubmitting: formik.isSubmitting,
    }
    switch (pageNumber) {
      case 1:
        return <RegisterFormOnePage {...props} />
      case 2:
        return <RegisterFormTwoPage {...props} />
      case 3:
        return <RegisterFormThreePage {...props} />
      case 4:
        return <RegisterFormFourPage {...props} error={error} setError={setError} />
      default:
        return null
    }
  }
  return (
    <AuthPage>
      <AuthForm>
        <AuthHeader title={word("auth.signup.form.title")} subtitle={word("auth.signup.form.enteringCommunity")} />
        <AuthContent>{FormPage()}</AuthContent>
      </AuthForm>
    </AuthPage>
  )
}
